import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import type { RouterHistory } from "@tanstack/react-router";
import { AppShell } from "@client/components/AppShell";
import { compareSearch, parseCompareIds } from "@client/lib/compareIds";
import type { AuthState } from "@client/lib/authContext";
import { AdminAllowListPage } from "@client/pages/AdminAllowListPage";
import { AdminAuditPage } from "@client/pages/AdminAuditPage";
import { AdminUsersPage } from "@client/pages/AdminUsersPage";
import { LoginPage } from "@client/pages/LoginPage";
import { ProductDetailPage } from "@client/pages/ProductDetailPage";
import { ProductNewPage } from "@client/pages/ProductNewPage";
import { ProductsPage } from "@client/pages/ProductsPage";
import { ScenarioComparePage } from "@client/pages/ScenarioComparePage";
import { ScenarioEditorPage } from "@client/pages/ScenarioEditorPage";
import { ScenarioVersionsPage } from "@client/pages/ScenarioVersionsPage";

/**
 * The route tree, with the session in the router's context so a guard is a
 * property of the route rather than something every page remembers to do.
 * Screens and access rules: docs/08-frontend.md#routes.
 */
export interface RouterContext {
  auth: AuthState;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-12">
      <h1 className="text-xl font-bold text-ink">Page not found</h1>
      <p className="mt-2 text-sm text-ink-soft">That address does not match anything here.</p>
    </main>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  // `next` is carried through the login so a deep link survives the detour. It is
  // sanitised at the point of use — see `safeNextPath`.
  validateSearch: (search: Record<string, unknown>): { next?: string } => ({
    next: typeof search.next === "string" ? search.next : undefined,
  }),
  component: LoginPage,
});

/**
 * A pathless layout route: everything nested under it requires a session, and the
 * check lives here once instead of in each page. `AuthProvider` has already
 * resolved `GET /api/auth/me` before the router renders, so this reads state
 * rather than waiting on a request — and the server re-checks both gates on every
 * call regardless, which is what actually enforces this.
 */
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_authed",
  beforeLoad: ({ context, location }) => {
    if (context.auth.status === "authenticated") return;
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- the router signals a redirect by throwing its own control-flow object, which is not an Error
    throw redirect({ to: "/login", search: { next: location.href }, replace: true });
  },
  component: AppShell,
});

const productsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/",
  component: ProductsPage,
});

/**
 * Declared before the `$slug` route it would otherwise collide with. The router
 * ranks static segments above dynamic ones regardless, so this is for the reader
 * rather than for the matcher.
 */
const productNewRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/products/new",
  component: ProductNewPage,
});

const productDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/products/$slug",
  component: ProductDetailPage,
});

const scenarioEditorRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/products/$slug/scenarios/$scenarioId",
  component: ScenarioEditorPage,
});

const scenarioVersionsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/products/$slug/scenarios/$scenarioId/versions",
  component: ScenarioVersionsPage,
});

/**
 * `?ids=a,b,c`. Normalised on the way in — see `parseCompareIds` for why the
 * list is one comma-joined string rather than a repeated parameter — and kept
 * as that same string in the URL, so a link copied out of the address bar is the
 * link that was pasted in.
 */
const compareRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/compare",
  validateSearch: (search: Record<string, unknown>): { ids: string } =>
    compareSearch(parseCompareIds(search.ids)),
  component: ScenarioComparePage,
});

/**
 * The admin section, guarded once for all three screens.
 *
 * A non-admin who types the URL is sent to the products list rather than shown a
 * refusal: the pages would be empty anyway — every one of their queries is a 403
 * — and a dead end is a worse answer than the page they can use. The server is
 * what enforces this; the redirect is manners.
 */
const adminRoute = createRoute({
  getParentRoute: () => authedRoute,
  id: "_admin",
  beforeLoad: ({ context }) => {
    if (context.auth.permissions.isAdmin) return;
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- the router signals a redirect by throwing its own control-flow object, which is not an Error
    throw redirect({ to: "/", replace: true });
  },
});

const adminAllowListRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/admin/allowlist",
  component: AdminAllowListPage,
});

const adminUsersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/admin/users",
  component: AdminUsersPage,
});

const adminAuditRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/admin/audit",
  component: AdminAuditPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  authedRoute.addChildren([
    productsRoute,
    productNewRoute,
    productDetailRoute,
    scenarioEditorRoute,
    scenarioVersionsRoute,
    compareRoute,
    adminRoute.addChildren([adminAllowListRoute, adminUsersRoute, adminAuditRoute]),
  ]),
]);

/**
 * The context is filled in by `RouterProvider` on every render, so the `undefined`
 * here is never observed — but a router has to be constructed before the React
 * tree exists, and this is the standard way to say so.
 */
export function createAppRouter(history?: RouterHistory) {
  return createRouter({
    routeTree,
    context: { auth: undefined as unknown as AuthState },
    ...(history ? { history } : {}),
  });
}

export const router = createAppRouter();

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
